import { scopeCoversPath, UNRESOLVED_OVERLAP_STATUSES, type IntentClaim, type Overlap } from "@botallion/contracts";
import { nameOf, outcomeLabel, overlapStatusLabel } from "../describe.ts";
import { html, type Html } from "../html.ts";
import type { DashboardState } from "../state.ts";
import { chip, clock, empty, type ViewContext } from "./common.ts";

type RowKind = "path" | "symbol" | "contract" | "migration" | "file";

interface Row {
  key: string;
  kind: RowKind;
  subject: string;
  /** Claims covering this row, by session. */
  cells: Record<string, IntentClaim[]>;
  /** Sessions whose pushes touched this file (file rows only). */
  pushed: Set<string>;
  overlaps: Overlap[];
}

const KIND_ORDER: RowKind[] = ["migration", "contract", "path", "symbol", "file"];
const KIND_LABEL: Record<RowKind, string> = { path: "path", symbol: "symbol", contract: "contract", migration: "lane", file: "pushed file" };

/** The rows an overlap lights up. Path subjects come as `a <> b` when the two globs differ. */
function overlapKeys(o: Overlap): string[] {
  switch (o.kind) {
    case "migration":
      return ["migration:migration"];
    case "path":
      return o.subjects.flatMap((s) => s.split(" <> ")).map((s) => `path:${s}`);
    case "symbol":
      return o.subjects.map((s) => `symbol:${s}`);
    case "contract":
      return o.subjects.map((s) => `contract:${s}`);
    case "diff":
      return o.subjects.flatMap((s) => [`file:${s}`, `symbol:${s}`, `contract:${s}`, `path:${s}`]);
  }
}

export function buildClaimRows(state: DashboardState): { rows: Row[]; columns: string[] } {
  const live = Object.values(state.claims).filter((c) => c.status === "active" || c.status === "queued");
  const overlaps = Object.values(state.overlaps).filter((o) => o.status !== "released");
  const rows = new Map<string, Row>();
  const row = (kind: RowKind, subject: string): Row => {
    const key = `${kind}:${subject}`;
    let r = rows.get(key);
    if (!r) rows.set(key, (r = { key, kind, subject, cells: {}, pushed: new Set(), overlaps: [] }));
    return r;
  };
  const add = (r: Row, c: IntentClaim) => (r.cells[c.sessionId] ??= []).push(c);
  for (const c of live) {
    for (const p of c.scope.paths) add(row("path", p), c);
    for (const s of c.scope.symbols) add(row("symbol", s), c);
    for (const k of c.scope.contracts) add(row("contract", k), c);
    if (c.scope.migration) add(row("migration", "migration"), c);
  }
  // Pushed files that collided get a row of their own, showing who claims them and who pushed them.
  for (const o of overlaps.filter((x) => x.kind === "diff")) {
    for (const file of o.subjects) {
      const existing = rows.get(`path:${file}`) ?? rows.get(`symbol:${file}`) ?? rows.get(`contract:${file}`);
      const r = existing ?? row("file", file);
      if (!existing) for (const c of live) if (scopeCoversPath(c.scope, file)) add(r, c);
      for (const d of state.diffs) if (d.files.some((f) => f.path === file)) r.pushed.add(d.sessionId);
    }
  }
  for (const o of overlaps) for (const k of overlapKeys(o)) rows.get(k)?.overlaps.push(o);

  const sessionIds = new Set<string>();
  for (const r of rows.values()) {
    for (const id of Object.keys(r.cells)) sessionIds.add(id);
    for (const id of r.pushed) sessionIds.add(id);
  }
  const columns = Object.values(state.sessions)
    .filter((s) => sessionIds.has(s.id))
    .sort((a, b) => a.startedAt.localeCompare(b.startedAt) || a.id.localeCompare(b.id))
    .map((s) => s.id);
  const sorted = [...rows.values()].sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || a.subject.localeCompare(b.subject));
  return { rows: sorted, columns };
}

function rowTone(r: Row): "block" | "warn" | "good" | "" {
  const open = r.overlaps.filter((o) => UNRESOLVED_OVERLAP_STATUSES.includes(o.status));
  if (open.some((o) => o.severity === "block")) return "block";
  if (open.length) return "warn";
  if (r.overlaps.length) return "good";
  return "";
}

function overlapCell(state: DashboardState, r: Row): Html {
  if (!r.overlaps.length) return html``;
  return html`${r.overlaps.map((o) => {
    const res = state.resolutions.filter((x) => x.overlapId === o.id).at(-1);
    const unresolved = UNRESOLVED_OVERLAP_STATUSES.includes(o.status);
    const label = unresolved
      ? `${o.severity === "block" ? "blocking" : "warning"}, ${overlapStatusLabel(o.status).toLowerCase()}`
      : `settled: ${res ? outcomeLabel(res.outcome) : "resolved"}`;
    const tone = unresolved ? (o.status === "escalated" ? "human" : o.severity === "block" ? "block" : "warn") : "good";
    return html`<div>${chip(label, tone)} <span class="muted">${nameOf(state, o.sessionA)} &amp; ${nameOf(state, o.sessionB)}</span></div>`;
  })}`;
}

/** View 2: which agent holds which paths, contracts and the migration lane, with overlaps highlighted. */
export function claimsView(ctx: ViewContext): Html {
  const { state } = ctx;
  const { rows, columns } = buildClaimRows(state);
  const lane = state.lane;
  const laneLine = html`<div class="lane"><strong>Migration lane:</strong>
    ${lane.holder ? html`${chip(nameOf(state, lane.holder.sessionId), "info")} holds it since ${clock(lane.holder.since)}` : html`<span class="muted">free</span>`}
    ${lane.queue.length ? html`<span class="queue">waiting: ${lane.queue.map((q, i) => html`${i ? ", " : ""}${nameOf(state, q.sessionId)}`)}</span>` : ""}
  </div>`;
  if (!rows.length) return html`${laneLine}${empty("No live claims. Agents claim their ground here before they edit. Nothing to do until one joins.")}`;
  return html`${laneLine}
  <table class="claims">
    <thead><tr><th>Claimed</th>${columns.map((id) => html`<th>${nameOf(state, id)}<div class="muted">${state.sessions[id]?.wpId ?? ""}</div></th>`)}<th>Overlap</th></tr></thead>
    <tbody>${rows.map((r) => html`<tr class="${rowTone(r)}">
      <td class="subject"><span class="kind">${KIND_LABEL[r.kind]}</span> <code>${r.kind === "migration" ? "migration lane" : r.subject}</code></td>
      ${columns.map((id) => {
        const cs = r.cells[id] ?? [];
        const pushed = r.pushed.has(id);
        return html`<td class="cell">${cs.map((c) =>
          chip(c.status === "queued" ? "queued" : c.mode, c.status === "queued" ? "warn" : c.mode === "exclusive" ? "info" : "good", c.rationale),
        )}${pushed ? chip(cs.length ? "pushed" : "pushed, unclaimed", cs.length ? "muted" : "block") : ""}</td>`;
      })}
      <td class="overlap">${overlapCell(state, r)}</td>
    </tr>`)}</tbody>
  </table>`;
}
